<script setup lang="ts">
  import { watch, reactive } from "vue";
  import { IconArrowLeft } from "@ui-kit";
  import { StatusBar, TopBar } from "@src/components";
  import { useItemList } from "../logic";
  import { default as FoundItem } from "./FoundItem.vue";
  import { default as FoundItemGroup } from "./FoundItemGroup.vue";
  import { default as UnlinkItemDialog } from "./UnlinkItemDialog.vue";

  const {
    zone_path,
    zone_id,
    found_item_ids = [],
    not_found_item_ids,
  } = defineProps<{
    zone_path: string;
    zone_id: string;
    not_found_item_ids: string[];
    found_item_ids: string[];
  }>();
  const emit = defineEmits<{ (e: "click:back"): void; (e: "unlink", item_id: string): void }>();

  const unlink_state = reactive<{ visible: boolean; item_id: string | null }>({ visible: false, item_id: null });

  const item_list = useItemList();

  const handleClickUnlink = (item_id: string) => {
    unlink_state.item_id = item_id;
    unlink_state.visible = true;
  };

  const handleClickCloseUnlinkDialog = () => {
    unlink_state.item_id = null;
    unlink_state.visible = false;
  };

  watch(
    () => found_item_ids.length,
    () => {
      item_list.fetch([...found_item_ids, ...not_found_item_ids]);
    },
    { immediate: true },
  );
</script>

<template>
  <div class="found-item-list" data-testid="found-item-list">
    <StatusBar />
    <TopBar :title="$t('Найденные цветомодели')" :subtitle="zone_path" @click:left="emit('click:back')">
      <template #left><IconArrowLeft /></template>
    </TopBar>
    <div v-if="!item_list.isEmpty()" class="found-item-list__content" data-testid="found-item-list-content">
      <FoundItemGroup :title="$t('Осталось найти')" :count="not_found_item_ids.length">
        <template v-for="item_id in not_found_item_ids" :key="item_id">
          <FoundItem
            v-if="item_list.state.map[item_id]"
            :image_url="item_list.state.map[item_id].image_url"
            :title="item_list.state.map[item_id].title"
            :article="item_list.state.map[item_id].article"
            :color="item_list.state.map[item_id].color"
            :link_created="item_list.state.map[item_id].link_created"
            @click:unlink="() => handleClickUnlink(item_id)"
            data-testid="not-found-item"
          />
        </template>
      </FoundItemGroup>
      <FoundItemGroup :title="$t('Найденные')" :count="found_item_ids.length">
        <template v-for="item_id in found_item_ids" :key="item_id">
          <FoundItem
            v-if="item_list.state.map[item_id]"
            :image_url="item_list.state.map[item_id].image_url"
            :title="item_list.state.map[item_id].title"
            :article="item_list.state.map[item_id].article"
            :color="item_list.state.map[item_id].color"
            :link_created="item_list.state.map[item_id].link_created"
            @click:unlink="() => handleClickUnlink(item_id)"
            data-testid="found-item"
          />
        </template>
      </FoundItemGroup>
    </div>
  </div>
  <UnlinkItemDialog
    :item_id="unlink_state.item_id ?? ''"
    :zone_id="zone_id"
    :visible="unlink_state.visible"
    @close="handleClickCloseUnlinkDialog"
    @unlink="() => emit('unlink', unlink_state.item_id)"
  />
</template>

<style scoped>
  .found-item-list {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .found-item-list__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
  }
</style>
